/**
 * Feature keys gate whole modules. A module is available to an organization when
 * its feature is enabled — which is the intersection of the subscription plan
 * and the business-type template (with per-org overrides on top).
 *
 * Domain code must never branch on business type; it branches on features.
 */
import { DEFAULT_CURRENCY } from './currency';
const FEATURE_CATALOG = {
    CORE: ['Core', 'Products, stock, sales and customers', true],
    POS: ['Point of Sale', 'Touch-optimised register and receipts', false],
    PROPERTY_SALES: ['Property & land sales', 'Plots, titles and instalment sales', false],
    MULTI_BRANCH: ['Multi-branch', 'More than one branch', false],
    MULTI_WAREHOUSE: ['Multi-warehouse', 'More than one warehouse and transfers', false],
    PURCHASING: ['Purchasing', 'Suppliers, purchase orders and goods receipts', true],
    BATCH_EXPIRY: ['Batches & Expiry', 'Batch numbers, expiry dates and alerts', false],
    SERIAL_NUMBERS: ['Serial Numbers', 'Serial/IMEI tracking and warranties', false],
    WARRANTY: ['Warranty', 'Warranty registration and claims', false],
    MANUFACTURING: ['Manufacturing', 'Bills of materials and production orders', false],
    RESTAURANT: ['Restaurant', 'Menus, recipes, tables and kitchen orders', false],
    DELIVERY: ['Delivery', 'Delivery orders, drivers and proof of delivery', false],
    PROMOTIONS: ['Promotions', 'Discount rules and campaigns', false],
    ADVANCED_PRICING: ['Advanced Pricing', 'Price lists, tiers and customer pricing', false],
    ACCOUNTING: ['Accounting', 'Receivables, payables and cash accounts', true],
    ADVANCED_REPORTS: ['Advanced Reports', 'Profitability, ageing and trend analysis', false],
    API_ACCESS: ['API Access', 'API keys and webhooks for integrations', false],
    IMPORT_EXPORT: ['Import & Export', 'Bulk CSV/Excel import and export', true],
    AI_INSIGHTS: ['AI Insights', 'Forecasting and natural-language queries', false],
};
export const FEATURES = Object.fromEntries(Object.keys(FEATURE_CATALOG).map((k) => [k, k]));
export const F = FEATURES;
export const FEATURE_DEFINITIONS = Object.entries(FEATURE_CATALOG).map(([key, [name, description, isCore]]) => ({
    key,
    name,
    description,
    isCore,
}));
/** Usage limits enforced by `LimitsService`. */
export const LIMITS = {
    USERS: 'maxUsers',
    BRANCHES: 'maxBranches',
    WAREHOUSES: 'maxWarehouses',
    PRODUCTS: 'maxProducts',
    MONTHLY_TRANSACTIONS: 'maxMonthlyTransactions',
    STORAGE_BYTES: 'maxStorageBytes',
};
const CORE_FEATURES = FEATURE_DEFINITIONS.filter((f) => f.isCore).map((f) => f.key);
/**
 * What the platform charges, in the platform's own currency.
 *
 * These figures were converted from the original dollar prices at roughly
 * 3,800 UGX and rounded to prices a business would recognise. They are a
 * starting point, not a pricing decision: change them here and they flow to
 * every new installation. A yearly price is ten monthly ones — two months free.
 */
export const PLAN_DEFINITIONS = [
    {
        code: 'STARTER',
        name: 'Starter',
        description: 'A single shop finding its feet.',
        monthlyPrice: 0,
        yearlyPrice: 0,
        currency: DEFAULT_CURRENCY,
        trialDays: 0,
        limits: {
            maxUsers: 3,
            maxBranches: 1,
            maxWarehouses: 1,
            maxProducts: 500,
            maxMonthlyTransactions: 1000,
            maxStorageBytes: 1024 * 1024 * 512,
        },
        features: [...CORE_FEATURES],
    },
    {
        code: 'BUSINESS',
        name: 'Business',
        description: 'A growing business with more than one location.',
        monthlyPrice: 110000,
        yearlyPrice: 1100000,
        currency: DEFAULT_CURRENCY,
        trialDays: 14,
        limits: {
            maxUsers: 10,
            maxBranches: 3,
            maxWarehouses: 5,
            maxProducts: 10000,
            maxMonthlyTransactions: 20000,
            maxStorageBytes: 1024 * 1024 * 1024 * 5,
        },
        features: [
            ...CORE_FEATURES,
            'MULTI_BRANCH',
            'MULTI_WAREHOUSE',
            'BATCH_EXPIRY',
            'PROMOTIONS',
            'ADVANCED_PRICING',
        ],
    },
    {
        code: 'PROFESSIONAL',
        name: 'Professional',
        description: 'Multi-branch operations with specialised workflows.',
        monthlyPrice: 300000,
        yearlyPrice: 3000000,
        currency: DEFAULT_CURRENCY,
        trialDays: 14,
        limits: {
            maxUsers: 40,
            maxBranches: 15,
            maxWarehouses: 25,
            maxProducts: 100000,
            maxMonthlyTransactions: 250000,
            maxStorageBytes: 1024 * 1024 * 1024 * 50,
        },
        features: [
            ...CORE_FEATURES,
            'MULTI_BRANCH',
            'MULTI_WAREHOUSE',
            'BATCH_EXPIRY',
            'SERIAL_NUMBERS',
            'WARRANTY',
            'PROMOTIONS',
            'ADVANCED_PRICING',
            'ADVANCED_REPORTS',
            'DELIVERY',
            'MANUFACTURING',
            'RESTAURANT',
            'API_ACCESS',
        ],
    },
    {
        code: 'ENTERPRISE',
        name: 'Enterprise',
        description: 'Unlimited scale with every module and AI insights.',
        monthlyPrice: 750000,
        yearlyPrice: 7500000,
        currency: DEFAULT_CURRENCY,
        trialDays: 30,
        limits: {
            maxUsers: null,
            maxBranches: null,
            maxWarehouses: null,
            maxProducts: null,
            maxMonthlyTransactions: null,
            maxStorageBytes: null,
        },
        features: FEATURE_DEFINITIONS.map((f) => f.key),
    },
];
//# sourceMappingURL=features.js.map